// src/components/TemplateSelector.jsx JB added this is used inside the dashboard /templates aka AI Designs
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { 
  Box, Flex, Heading, Text, Button, Image, SimpleGrid, Stack, Spinner,
  Badge, useColorModeValue, Icon, VStack, HStack
} from '@chakra-ui/react';
import './TemplateSelector.css';

const TemplateSelector = ({ uploadedImageURL, hideHeader = false, showHowItWorks = true }) => {
  const [templates, setTemplates] = useState([]);
  const [eventTemplates, setEventTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);

        /* 1. PUBLIC TEMPLATES (same logic as before) */
        const { data: publicData, error: publicError } = await supabase
          .from('templates')
          .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
          .eq('is_public', true)
          .eq('style_subcategory', 'Baseball/Softball')
          .order('updated_at', { ascending: false });

        if (publicError) throw publicError;
        setTemplates(publicData || []);

        /* 2. EVENT-SPECIFIC TEMPLATES FOR THIS USER */
        const { data: sessionData } = await supabase.auth.getSession();
        const user = sessionData?.session?.user;

        if (user) {
          // Get all events the user is registered for
          const { data: orders, error: ordersError } = await supabase
            .from('event_orders')
            .select('event_id')
            .eq('user_id', user.id);

          if (ordersError) throw ordersError;

          const eventIds = Array.from(new Set((orders || []).map((o) => o.event_id))).filter(Boolean);

          if (eventIds.length > 0) {
            // Fetch event details to get template mappings
            const { data: eventsData, error: eventsError } = await supabase
              .from('events')
              .select('id, name, templates')
              .in('id', eventIds);

            if (eventsError) throw eventsError;

            // Build a lookup of event → templateIds
            const eventTemplateLookup = {};
            let allTemplateIds = [];

            (eventsData || []).forEach((evt) => {
              if (evt.templates) {
                const ids = evt.templates
                  .split(',')
                  .map((id) => id.trim())
                  .filter(Boolean);

                if (ids.length > 0) {
                  eventTemplateLookup[evt.id] = { name: evt.name, ids };
                  allTemplateIds = allTemplateIds.concat(ids);
                }
              }
            });

            allTemplateIds = Array.from(new Set(allTemplateIds));

            if (allTemplateIds.length > 0) {
              const { data: eventTplData, error: eventTplError } = await supabase
                .from('templates')
                .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
                .in('id', allTemplateIds);

              if (eventTplError) throw eventTplError;

              // Group templates by event name for easier rendering
              const grouped = {};
              Object.values(eventTemplateLookup).forEach(({ name, ids }) => {
                const matchingTemplates = eventTplData.filter((t) => ids.includes(String(t.id)));
                if (matchingTemplates.length > 0) {
                  grouped[name] = matchingTemplates;
                }
              });

              setEventTemplates(grouped);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError('Failed to load templates. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);

  const handleSelect = (templateId) => {
    navigate(`/customize/${templateId}`, { state: { uploadedImageURL } });
  };

  if (loading) {
    return (
      <Box minH="50vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner thickness="4px" speed="0.65s" size="xl" color="blue.500" />
          <Text color="gray.600" fontWeight="medium">Loading AI Designs...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="50vh" display="flex" alignItems="center" justifyContent="center" px={4}>
        <VStack spacing={4} textAlign="center" maxW="md">
          <Box bg="red.100" p={4} borderRadius="full">
            <Text fontSize="32px">⚠️</Text>
          </Box>
          <Heading size="md" color="gray.800">Something went wrong</Heading>
          <Text color="gray.600">{error}</Text>
          <Button colorScheme="blue" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" pt={4} pb={8}>
      {!hideHeader && (
        <Box bg="white" boxShadow="sm" mb={8} px={4} py={6}>
          <Box maxW="7xl" mx="auto">
            <VStack spacing={4} textAlign="center">
              <Box bg="gradient" bgGradient="linear(135deg, blue.500, purple.600)" p={3} borderRadius="xl">
                <Text fontSize="32px">🎨</Text>
              </Box>
              <Box>
                <Heading size="xl" color="gray.800" mb={2}>Choose Your AI Design</Heading>
                <Text color="gray.600" fontSize="lg" maxW="2xl" mx="auto">
                  Professional sports layouts that automatically fit your athlete photos. 
                  Pick a design and customize it with your images and details.
                </Text>
              </Box>
              <HStack spacing={4} fontSize="sm" color="gray.500">
                <Text>Step 1 of 2</Text>
                <Badge colorScheme="blue" variant="subtle">AI-Powered</Badge>
              </HStack>
            </VStack>
          </Box>
        </Box>
      )}

      <Box maxW="7xl" mx="auto" px={4}>
        {/* How It Works Section */}
        {showHowItWorks && !hideHeader && (
          <Box bg="white" borderRadius="xl" p={6} mb={8} boxShadow="sm" border="1px" borderColor="gray.200">
            <Flex align="center" mb={4}>
              <Box bg="blue.100" p={2} borderRadius="md" mr={3}>
                <Text fontSize="20px">✨</Text>
              </Box>
              <Heading size="md" color="gray.800">How It Works</Heading>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <VStack spacing={2} textAlign="center">
                <Box bg="green.100" p={3} borderRadius="full">
                  <Text fontSize="24px">1️⃣</Text>
                </Box>
                <Text fontWeight="semibold" color="gray.800">Pick a Design</Text>
                <Text fontSize="sm" color="gray.600">Choose from professional sports layouts</Text>
              </VStack>
              <VStack spacing={2} textAlign="center">
                <Box bg="blue.100" p={3} borderRadius="full">
                  <Text fontSize="24px">2️⃣</Text>
                </Box>
                <Text fontWeight="semibold" color="gray.800">Add Your Photos</Text>
                <Text fontSize="sm" color="gray.600">Upload athlete photos and customize text</Text>
              </VStack>
              <VStack spacing={2} textAlign="center">
                <Box bg="purple.100" p={3} borderRadius="full">
                  <Text fontSize="24px">3️⃣</Text>
                </Box>
                <Text fontWeight="semibold" color="gray.800">Get Pro Results</Text>
                <Text fontSize="sm" color="gray.600">Download high-quality designs instantly</Text>
              </VStack>
            </SimpleGrid>
          </Box>
        )}

        {/* Event Specific Templates Grid (grouped by event) */}
        {Object.keys(eventTemplates).length > 0 && (
          Object.entries(eventTemplates).map(([evtName, evtTemplatesArr]) => (
            <Box key={evtName} mb={10}>
              <Box mb={6}>
                <Flex align="center" mb={2}>
                  <Box bg="teal.100" p={2} borderRadius="md" mr={3}>
                    <Text fontSize="20px">🏟️</Text>
                  </Box>
                  <Heading size="lg" color="gray.800">{evtName} Designs</Heading>
                </Flex>
                <Text color="gray.600" mb={4}>
                  Exclusive designs available for your {evtName} event.
                </Text>
                <HStack spacing={4} mb={4}>
                  <Badge colorScheme="green" variant="outline">{evtTemplatesArr.length} designs</Badge>
                  <Badge colorScheme="purple" variant="outline">Event Specific</Badge>
                </HStack>
              </Box>

              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
                {evtTemplatesArr.map((template) => (
                  <Box
                    key={template.id}
                    bg="white"
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="lg"
                    border="1px"
                    borderColor="gray.200"
                    _hover={{
                      transform: 'translateY(-4px)',
                      boxShadow: 'xl',
                      borderColor: 'blue.300',
                    }}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    cursor="pointer"
                    onClick={() => handleSelect(template.id)}
                  >
                    <Box position="relative" overflow="hidden">
                      <Image
                        src={template.thumbnail_url}
                        alt={template.name}
                        w="100%"
                        h="300px"
                        objectFit="contain"
                        bg="gray.50"
                      />
                      <Box
                        position="absolute"
                        inset="0"
                        bg="blackAlpha.700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        opacity="0"
                        _hover={{ opacity: 1 }}
                        transition="opacity 0.3s"
                      >
                        <VStack spacing={3}>
                          <Button
                            colorScheme="blue"
                            size="lg"
                            leftIcon={<Text fontSize="16px">✨</Text>}
                            fontWeight="semibold"
                          >
                            Customize This Design
                          </Button>
                          <Text color="white" fontSize="sm" textAlign="center" px={4}>
                            Add your photos and personalize the text
                          </Text>
                        </VStack>
                      </Box>
                    </Box>

                    <Box p={4}>
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1} flex="1">
                          <Text fontWeight="semibold" color="gray.800" fontSize="lg">
                            {template.name || 'Professional Design'}
                          </Text>
                          <HStack spacing={2}>
                            <Badge colorScheme="blue" size="sm">
                              AI-Enhanced
                            </Badge>
                            <Badge colorScheme="green" size="sm">
                              Ready to Use
                            </Badge>
                          </HStack>
                        </VStack>
                        <Button
                          colorScheme="blue"
                          variant="ghost"
                          size="sm"
                          rightIcon={<Text fontSize="12px">→</Text>}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(template.id);
                          }}
                        >
                          Start
                        </Button>
                      </HStack>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          ))
        )}

        {/* Public Templates Grid */}
        {templates.length > 0 ? (
          <>
            <Box mb={6}>
              <Flex align="center" mb={2}>
                <Box bg="orange.100" p={2} borderRadius="md" mr={3}>
                  <Text fontSize="20px">🏆</Text>
                </Box>
                <Heading size="lg" color="gray.800">Available Designs</Heading>
              </Flex>
              <Text color="gray.600" mb={4}>
                Click any design below to start customizing with your athlete photos
              </Text>
              <HStack spacing={4} mb={4}>
                <Badge colorScheme="green" variant="outline">{templates.length} designs available</Badge>
                <Badge colorScheme="blue" variant="outline">Baseball/Softball</Badge>
              </HStack>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
              {templates.map((template) => (
                <Box
                  key={template.id}
                  bg="white"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  _hover={{ 
                    transform: 'translateY(-4px)', 
                    boxShadow: 'xl',
                    borderColor: 'blue.300'
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  cursor="pointer"
                  onClick={() => handleSelect(template.id)}
                >
                  <Box position="relative" overflow="hidden">
                    <Image 
                      src={template.thumbnail_url} 
                      alt={template.name}
                      w="100%"
                      h="300px"
                      objectFit="contain"
                      bg="gray.50"
                    />
                    <Box
                      position="absolute"
                      inset="0"
                      bg="blackAlpha.700"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      opacity="0"
                      _hover={{ opacity: 1 }}
                      transition="opacity 0.3s"
                    >
                      <VStack spacing={3}>
                        <Button
                          colorScheme="blue"
                          size="lg"
                          leftIcon={<Text fontSize="16px">✨</Text>}
                          fontWeight="semibold"
                        >
                          Customize This Design
                        </Button>
                        <Text color="white" fontSize="sm" textAlign="center" px={4}>
                          Add your photos and personalize the text
                        </Text>
                      </VStack>
                    </Box>
                  </Box>
                  
                  <Box p={4}>
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={1} flex="1">
                        <Text fontWeight="semibold" color="gray.800" fontSize="lg">
                          {template.name || 'Professional Design'}
                        </Text>
                        <HStack spacing={2}>
                          <Badge colorScheme="blue" size="sm">AI-Enhanced</Badge>
                          <Badge colorScheme="green" size="sm">Ready to Use</Badge>
                        </HStack>
                      </VStack>
                      <Button
                        colorScheme="blue"
                        variant="ghost"
                        size="sm"
                        rightIcon={<Text fontSize="12px">→</Text>}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(template.id);
                        }}
                      >
                        Start
                      </Button>
                    </HStack>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </>
        ) : (
          <Box bg="white" borderRadius="xl" p={8} textAlign="center" boxShadow="sm" border="1px" borderColor="gray.200">
            <VStack spacing={4}>
              <Box bg="gray.100" p={4} borderRadius="full">
                <Text fontSize="48px" opacity="0.5">🎨</Text>
              </Box>
              <Heading size="md" color="gray.800">No Designs Available Yet</Heading>
              <Text color="gray.600" maxW="md">
                AI Designs will appear here once they're created. These are professional sports layouts 
                that automatically work with your athlete photos.
              </Text>
              <Button as={Link} to="/editor" colorScheme="blue" size="lg">
                Create New Design
              </Button>
            </VStack>
          </Box>
        )}

        {/* Pro Tips Section */}
        <Box bg="gradient" bgGradient="linear(135deg, blue.500, purple.600)" borderRadius="xl" p={6} mt={8} color="white">
          <Flex align="center" mb={4}>
            <Text fontSize="24px" mr={3}>💡</Text>
            <Heading size="md">Pro Tips for Best Results</Heading>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} fontSize="sm">
            <Text>• Use high-resolution athlete photos for crisp results</Text>
            <Text>• Portrait orientation photos work best for most designs</Text>
            <Text>• Keep backgrounds simple - the AI will enhance them</Text>
            <Text>• Try different designs to see which fits your style</Text>
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateSelector;